import { Nav, Navbar } from 'react-bootstrap';
import { Route, RouteItems } from 'sitemap';
import { capitalize } from 'helpers/utils';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import {
  UilArrowFromRight,
  UilLeftArrowToLeft,
  UilSignOutAlt
} from '@iconscout/react-unicons';
import { useAppContext } from 'providers/AppProvider';
import { useAuth } from 'providers/AuthProvider';
import Button from 'components/base/Button';
import NavbarVerticalCollapseProvider from './NavbarVerticalCollapseProvider';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthRole } from 'features/auth/authSession';
import {
  LOCUM_TAB_LABELS,
  PHARMACIST_TAB_LABELS,
  ADMIN_TAB_LABELS
} from 'pages/app/dashboard/shared/tabLabels';

const pharmacistMenuPages: Route[] = [
  {
    name: PHARMACIST_TAB_LABELS.dashboard,
    icon: 'pie-chart',
    path: '/dashboard/pharmacy',
    active: true
  },
  {
    name: PHARMACIST_TAB_LABELS.createShift,
    icon: 'plus-circle',
    path: '/dashboard/pharmacy/create-shift',
    active: true
  },
  {
    name: PHARMACIST_TAB_LABELS.manageShifts,
    icon: 'clipboard',
    path: '/dashboard/pharmacy/manage-shifts',
    active: true
  },
  {
    name: PHARMACIST_TAB_LABELS.bookingsApplicants,
    icon: 'users',
    path: '/dashboard/pharmacy/bookings',
    active: true
  },
  {
    name: PHARMACIST_TAB_LABELS.profile,
    icon: 'user',
    path: '/dashboard/pharmacy/profile',
    active: true
  }
];

const locumMenuPages: Route[] = [
  {
    name: LOCUM_TAB_LABELS.dashboard,
    icon: 'pie-chart',
    path: '/dashboard/locum',
    active: true
  },
  {
    name: LOCUM_TAB_LABELS.findShifts,
    icon: 'search',
    path: '/dashboard/locum/find-shifts',
    active: true
  },
  {
    name: LOCUM_TAB_LABELS.myBookings,
    icon: 'calendar',
    path: '/dashboard/locum/bookings',
    active: true
  },
  {
    name: LOCUM_TAB_LABELS.profile,
    icon: 'user',
    path: '/dashboard/locum/profile',
    active: true
  }
];

const adminMenuPages: Route[] = [
  {
    name: ADMIN_TAB_LABELS.dashboard,
    icon: 'pie-chart',
    path: '/dashboard/admin',
    active: true
  },
  {
    name: ADMIN_TAB_LABELS.userManagement,
    icon: 'users',
    path: '#',
    active: true,
    pages: [
      {
        name: ADMIN_TAB_LABELS.manageLocum,
        path: '/dashboard/admin/user-management/manage-locum',
        active: true
      },
      {
        name: ADMIN_TAB_LABELS.managePharmacy,
        path: '/dashboard/admin/user-management/manage-pharmacy',
        active: true
      }
    ]
  },
  {
    name: ADMIN_TAB_LABELS.reports,
    icon: 'file-text',
    path: '#',
    active: true,
    pages: [
      {
        name: ADMIN_TAB_LABELS.userReport,
        path: '/dashboard/admin/reports/user-report',
        active: true
      },
      {
        name: ADMIN_TAB_LABELS.shiftReport,
        path: '/dashboard/admin/reports/shift-report',
        active: true
      },
      {
        name: ADMIN_TAB_LABELS.bookingReport,
        path: '/dashboard/admin/reports/booking-report',
        active: true
      }
    ]
  }
];

const NavbarVerical = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const {
    config: {
      openNavbarVertical,
      navbarVerticalAppearance,
      isNavbarVerticalCollapsed
    },
    setConfig
  } = useAppContext();

  const role = getAuthRole();

  const sidebarRoutes = useMemo<RouteItems[]>(() => {
    const pages =
      role === 'pharmacy'
        ? pharmacistMenuPages
        : role === 'locum'
        ? locumMenuPages
        : role === 'admin'
        ? adminMenuPages
        : [];

    return [
      {
        label: 'dashboard',
        icon: UilSignOutAlt,
        labelDisabled: true,
        active: true,
        pages
      }
    ];
  }, [role]);

  const handleLogout = () => {
    logout();
    if (role === 'admin') {
      navigate('/auth/admin-login', { replace: true });
    } else {
      navigate('/auth/login', { replace: true });
    }
  };

  return (
    <NavbarVerticalCollapseProvider>
      <Navbar
        className="navbar-vertical"
        expand="lg"
        variant=""
        data-navbar-appearance={
          navbarVerticalAppearance === 'darker' ? 'darker' : ''
        }
      >
        <Navbar.Collapse id="navbarVerticalCollapse" in={openNavbarVertical}>
          <div className="navbar-vertical-content">
            <Nav className="flex-column" as="ul" id="navbarVerticalNav">
              {sidebarRoutes.map(route => (
                <Nav.Item key={route.label}>
                  {!route.labelDisabled && (
                    <>
                      <p className="navbar-vertical-label">
                        {capitalize(route.label)}
                      </p>
                      <hr className="navbar-vertical-line" />
                    </>
                  )}
                  <NavbarVerticalMenu level={1} routes={route.pages} />
                </Nav.Item>
              ))}
              <Nav.Item as="li" className="nav-item-wrapper">
                <button
                  type="button"
                  className="nav-link label-1 w-100 text-start border-0 bg-transparent"
                  onClick={handleLogout}
                >
                  <div className="d-flex align-items-center">
                    <span className="nav-link-icon">
                      <UilSignOutAlt size={16} />
                    </span>
                    <span className="nav-link-text">Logout</span>
                  </div>
                </button>
              </Nav.Item>
            </Nav>
          </div>
        </Navbar.Collapse>
        <div className="navbar-vertical-footer">
          <Button
            className="navbar-vertical-toggle border-0 fw-semibold w-100 white-space-nowrap d-flex align-items-center"
            onClick={() => {
              setConfig({
                isNavbarVerticalCollapsed: !isNavbarVerticalCollapsed
              });
            }}
          >
            {isNavbarVerticalCollapsed ? (
              <UilArrowFromRight size={16} className="mb-1" />
            ) : (
              <>
                <UilLeftArrowToLeft size={16} className="mb-1" />
                <span className="ms-2">Collapsed View</span>
              </>
            )}
          </Button>
        </div>
      </Navbar>
    </NavbarVerticalCollapseProvider>
  );
};

export default NavbarVerical;
